import { AppBarSdk } from 'app/src/features/Home/components/AppBar/AppBarSdk'
import { Image } from 'app/src/shared/components'
import { getFunnelDiscount, isDemoApp } from 'app/src/shared/helpers'
import { AppBarPaths, useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import pws from 'app/static/images/pws.png'
import { MenuItem } from 'primereact/menuitem'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

export function AppBar({ setDialogVisible }: { setDialogVisible: (visible: boolean) => void }) {
  const { t } = useTranslation('home', { keyPrefix: 'AppBar' })
  const welfareContext = useWebAppContext()
  const navigate = useWelfarePortalNavigate()
  const location = useLocation()
  const start = (
    <div className='hidden place-content-center sm:grid md:ml-4 md:mr-4' role='img' aria-label={t('AltLogoImage')}>
      <Image src={pws} alt={t('AltLogoImage')} pt={{ image: { className: 'mr-0 h-6' } }} />
    </div>
  )

  const funnelDiscount = getFunnelDiscount(welfareContext)

  const tabMenuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [
      {
        className: 'font-semibold',
        label: t('Landing'),
        command: () => {
          if (!welfareContext.userInfo.isForbidden) navigate.goToLanding()
        },
        data: { active: navigate.isActivePath(AppBarPaths.Landing, location.pathname) },
      },
    ]

    if (!welfareContext.userInfo.isForbidden && !welfareContext.userInfo.canSelfRegister) {
      if (!funnelDiscount.onlyDiscount) {
        items.push({
          className: 'font-semibold',
          label: t('Catalog'),
          command: () => navigate.goToCatalog(),
          data: {
            active: navigate.isActivePath(AppBarPaths.Catalog, location.pathname, location.search),
          },
        })
      }

      if (funnelDiscount.haveDiscount) {
        items.push({
          className: 'font-semibold',
          label: t('Discounts'),
          command: () => navigate.goToDiscount(),
          data: { active: navigate.isActivePath(AppBarPaths.Discount, location.pathname, location.search) },
        })
      }

      if (welfareContext.userInfo.services?.mealVouchers) {
        items.push({
          className: 'font-semibold',
          label: t('MealVouchers'),
          command: () => navigate.goToMealVouchers(),
          data: { active: navigate.isActivePath(AppBarPaths.MealVouchers, location.pathname, location.search) },
        })
      }

      items.push({
        className: 'font-semibold hidden xl:inline',
        label: t('UserInfo'),
        command: () => navigate.goToUserInfo(),
        data: { active: navigate.isActivePath(AppBarPaths.UserInfo, location.pathname) },
      })

      items.push({
        className: 'font-semibold',
        label: t('UserAccount'),
        command: () => navigate.goToUserAccount(),
        data: { active: navigate.isActivePath(AppBarPaths.UserAccount, location.pathname) },
      })
      // items.push({
      //   className: 'font-semibold',
      //   label: t('HowToUsePlatform'),
      //   command: () => navigate.goToTutorial(),
      //   data: { active: navigate.isActivePath(AppBarPaths.Tutorial, location.pathname) },
      // })
    }

    return items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navigate, t, welfareContext.userInfo.isForbidden])

  const menubarItems: MenuItem[] = [
    {
      icon: 'pi pi-question-circle text-2xl',
      className:
        'font-semibold [&_.p-menuitem-text]:sr-only [&_.p-menuitem-text]:2xl:not-sr-only [&_.p-menuitem-text]:2xl:inline',
      label: t('ContactUs'),
      command: () => welfareContext.update({ showContactUs: true }),
    },
  ]

  if (!welfareContext.userInfo.isForbidden && !welfareContext.userInfo.canSelfRegister) {
    menubarItems.push({
      icon: 'pi pi-user text-2xl',
      className:
        'font-semibold [&_.p-menuitem-text]:sr-only [&_.p-menuitem-text]:2xl:not-sr-only [&_.p-menuitem-text]:2xl:inline',
      label: welfareContext?.userInfo.firstName,

      command: () => welfareContext.update({ showUserSidePanel: true }),
    })
  }
  if (isDemoApp()) {
    menubarItems.push({
      icon: 'pi pi-cog text-2xl',
      className:
        'font-semibold [&_.p-menuitem-text]:sr-only [&_.p-menuitem-text]:2xl:not-sr-only [&_.p-menuitem-text]:2xl:inline',
      label: t('DemoSettings'),
      command: () => {
        setDialogVisible(true)
      },
    })
  }

  return (
    <div className='noPrint bg-[var(--card-background)] pl-2 lg:pl-4 xl:pl-8'>
      <div className='block lg:hidden'>
        <AppBarSdk
          data-testid='AppBar'
          className='mx-auto max-w-[var(--max-application-size)]'
          start={start}
          tabMenuItems={{ data: tabMenuItems.filter((x) => x.data?.active), type: 'menuItem' }}
          menubarItems={[...tabMenuItems.filter((x) => !x.data?.active), ...menubarItems]}
          pt={{
            menubar: {
              root: {
                className: 'min-w-[150px] md:min-w-[200px] min-h-[70px] justify-center',
              },
              menuitem: {
                tabIndex: 0,
                className: 'focus:outline-2 focus:outline-blue-500 focus:outline-offset-2',
                onKeyDown: (event: React.KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    event.stopPropagation()

                    // Trova l'elemento corrispondente tramite il testo
                    const target = event.currentTarget as HTMLElement
                    const elementText = target.textContent?.trim()

                    // Trova il menuItem corrispondente e esegui il comando
                    const allItems = [...tabMenuItems.filter((x) => !x.data?.active), ...menubarItems]
                    const matchingItem = allItems.find(
                      (item) =>
                        item.label === elementText ||
                        (item.label === t('ContactUs') && elementText?.includes('question-circle')) ||
                        (item.label === welfareContext?.userInfo.firstName && elementText?.includes('user')),
                    )

                    if (matchingItem?.command) {
                      const commandEvent = {
                        originalEvent: event,
                        item: matchingItem,
                      }
                      matchingItem.command(commandEvent)
                    }
                  }
                },
              },
            },
            tabMenu: {
              root: {
                className: 'pl-4',
              },
              menu: {
                role: 'menubar',
              },
            },
          }}
        />
      </div>
      <div className='hidden lg:block'>
        <AppBarSdk
          data-testid='AppBar'
          className='mx-auto max-w-[var(--max-application-size)]'
          start={start}
          tabMenuItems={{ data: tabMenuItems, type: 'menuItem' }}
          menubarItems={menubarItems}
          pt={{
            menubar: {
              root: {
                className: 'w-auto min-h-[70px]',
              },
              menu: {
                className: 'flex-nowrap',
              },
              menuitem: {
                tabIndex: 0,
                className: 'focus:outline-2 focus:outline-blue-500 focus:outline-offset-2',
                onKeyDown: (event: React.KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    event.stopPropagation()

                    // Trova l'elemento corrispondente tramite il testo
                    const target = event.currentTarget as HTMLElement
                    const elementText = target.textContent?.trim()

                    // Trova il menuItem corrispondente e esegui il comando
                    const allItems = [...tabMenuItems.filter((x) => !x.data?.active), ...menubarItems]
                    const matchingItem = allItems.find(
                      (item) =>
                        item.label === elementText ||
                        (item.label === t('ContactUs') && elementText?.includes('question-circle')) ||
                        (item.label === welfareContext?.userInfo.firstName && elementText?.includes('user')),
                    )

                    if (matchingItem?.command) {
                      const commandEvent = {
                        originalEvent: event,
                        item: matchingItem,
                      }
                      matchingItem.command(commandEvent)
                    }
                  }
                },
              },
            },
            tabMenu: {
              menu: {
                role: 'menubar',
              },
            },
          }}
        />
      </div>
    </div>
  )
}
